import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Icon,
  Button,
  Tabs,
  TabPanels,
  TabPanel,
  Circle,
  Progress,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  FaPlay,
  FaChalkboardTeacher,
  FaBrain,
  FaRocket,
  FaPoll,
  FaLightbulb,
  FaCode,
  FaBook,
  FaClock,
  FaCheck,
  FaArrowRight,
  FaArrowLeft,
  FaUsers,
  FaGraduationCap,
  FaTimes
} from 'react-icons/fa';
//import {useAppData} from '../../store/dataStore';

const MotionBox = motion(Box);
const MotionCircle = motion(Circle);

// Steps for fixed header
const steps = [
  { id: 1, title: 'Intro', icon: FaPlay },
  { id: 2, title: 'Timeline', icon: FaClock },
  { id: 3, title: 'Concept Flow', icon: FaBrain },
  { id: 4, title: 'Mini-Project', icon: FaCode },
  { id: 5, title: 'Why It Matters', icon: FaGraduationCap },
];

// Session Phase Data with Quiz
const sessionPhases = [
  {
    id: 1,
    title: 'Recap & Connect',
    duration: '5 min',
    icon: FaChalkboardTeacher,
    description: 'One team member presents previous session concepts',
    details: ['Quick knowledge check', 'Connect previous learning', 'Set context for today'],
  },
  {
    id: 2,
    title: 'Context Setting',
    duration: '5 min',
    icon: FaLightbulb,
    description: 'Real-world use cases & why this matters',
    details: ['Industry applications', 'Problem statement', 'Learning objectives'],
  },
  {
    id: 3,
    title: 'Core Concepts',
    duration: '40 min',
    icon: FaBrain,
    description: '4 foundational concepts with interactive learning',
    details: ['Background & theory', 'Live examples', 'Anonymous polls', 'Visual animations'],
    isExpandable: true,
  },
  {
    id: 4,
    title: 'Advanced Deep-Dive',
    duration: '15 min',
    icon: FaRocket,
    description: '1 advanced concept for real-world mastery',
    details: ['Complex scenarios', 'Edge cases', 'Best practices', 'Industry insights'],
  },
  {
    id: 5,
    title: 'Hands-On Mini-Project',
    duration: '15 min',
    icon: FaCode,
    description: 'Build real solution step-by-step',
    details: ['Problem context', 'Implementation walkthrough', 'Input → Output demo', 'Q&A'],
    isExpandable: true,
  },
  {
    id: 6,
    title: 'Self-Learn Path',
    duration: '10 min',
    icon: FaBook,
    description: 'Resources & assignments for deeper learning',
    details: ['Curated resources', 'Practice assignments', 'Reading materials', 'Next session prep'],
  },
  {
    id: 7,
    title: 'Quiz & Review',
    duration: '5 min',
    icon: FaPoll,
    description: 'Final knowledge check via interactive poll',
    details: ['Multiple-choice questions', 'Explain wrong answers', 'Reinforce key concepts', 'Session wrap-up'],
  },
];

// Updated Concept Learning Flow
const conceptFlow = [
  { step: 'Background', icon: FaBook, desc: 'Theory & foundation' },
  { step: 'Theory', icon: FaBrain, desc: 'Core principles' },
  { step: 'Real Example', icon: FaLightbulb, desc: 'Industry use case' },
  { step: 'Example Code', icon: FaCode, desc: 'Practical implementation' },
  { step: 'Check Understanding', icon: FaPoll, desc: 'Interactive poll' },
  { step: 'Simplified Demo', icon: FaUsers, desc: 'Re-explain if needed' },
  { step: 'Visual Demo', icon: FaPlay, desc: 'Animated explanation' },
  { step: 'Practice', icon: FaGraduationCap, desc: 'Assignment given' },
];

const SessionFlowModal = ({ isOpen, onClose }) => {
  //const { appData } = useAppData();
  //const { studentsCount } = appData;
  const [activeTab, setActiveTab] = useState(0);
  const [hoveredPhase, setHoveredPhase] = useState(null);

  const nextTab = () => {
    setActiveTab((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const prevTab = () => {
    setActiveTab((prev) => Math.max(prev - 1, 0));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent
        maxW={{ base: '100vw', md: '90vw' }}
        maxH={{ base: '100vh', md: '90vh' }}
        h={{ base: '100vh', md: 'auto' }}
        borderRadius={{ base: '0', md: '2xl' }}
        margin={0}
      >
        {/* Fixed Header with Steps */}
        <Box
          position="sticky"
          top={0}
          bg="brand.800"
          color="white"
          borderTopRadius="2xl"
          zIndex={10}
          px={8}
          py={4}
        >
          <VStack spacing={4}>
            {/* Progress Bar */}
            <Progress
              value={((activeTab + 1) / steps.length) * 100}
              size="sm"
              colorScheme="green"
              bg="brand.700"
              borderRadius="full"
              w="full"
            />

            {/* Steps Navigation */}


            <HStack justify="space-between" w="full" spacing={2}>
              <Button
                variant="ghost"
                color="white"
                onClick={prevTab}
                isDisabled={activeTab === 0}
                _hover={{ bg: 'brand.700' }}
                size="sm"
                minW="auto"
                px={2}
              >
                <Icon as={FaArrowLeft} boxSize={4} />
              </Button>

              <HStack
                spacing={{ base: 2, md: 4 }}
                flex={1}
                justify="center"
                maxW="calc(100% - 80px)" // Prevent overflow
              >
                {steps.map((step, index) => (
                  <Box
                    key={step.id}
                    cursor="pointer"
                    onClick={() => setActiveTab(index)}
                    opacity={activeTab === index ? 1 : 0.6}
                    transition="all 0.3s"
                    _hover={{ opacity: 1 }}
                    flexShrink={0} // Prevent shrinking
                  >
                    <Circle
                      size={{ base: '32px', md: '40px' }}
                      bg={activeTab === index ? 'accent.green' : 'brand.700'}
                      color="white"
                      border="2px solid"
                      borderColor={activeTab === index ? 'accent.green' : 'brand.600'}
                    >
                      <Icon as={step.icon} boxSize={{ base: 3, md: 4 }} />
                    </Circle>
                  </Box>
                ))}
              </HStack>

              <Button
                variant="ghost"
                color="white"
                onClick={nextTab}
                isDisabled={activeTab === steps.length - 1}
                _hover={{ bg: 'brand.700' }}
                size="sm"
                minW="auto"
                px={2}
              >
                <Icon as={FaArrowRight} boxSize={4} />
              </Button>


              <Button
                variant="ghost"
                color="white"
                onClick={onClose} // Changed from nextTab to onClose
                _hover={{ bg: 'brand.700' }}
                size="sm"
                minW="auto"
                px={2}
              >
                <Icon as={FaTimes} boxSize={4} /> {/* Changed from FaArrowRight to FaTimesCircle */}
              </Button>



            </HStack>


          </VStack>
        </Box>

        <ModalBody p={0}>
          <Tabs index={activeTab} onChange={setActiveTab} variant="unstyled">
            <TabPanels>
              {/* Step 1: Intro */}
              <TabPanel p={0}>
                <Box
                  minH="70vh"
                  bgGradient="linear(to-br, brand.700, brand.900, accent.green)"
                  position="relative"
                  overflow="hidden"
                >
                  {/* Blur effects */}
                  <Box
                    position="absolute"
                    top="10%"
                    right="10%"
                    w="300px"
                    h="300px"
                    bg="accent.gold"
                    opacity="0.1"
                    borderRadius="full"
                    filter="blur(80px)"
                  />
                  <Container
                    maxW="container.xl"
                    py={{ base: 4, md: 20 }}
                    width="100%"
                  >
                    <VStack spacing={8} textAlign="center" color="white">
                      <MotionBox
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                      >
                        <Heading as="h1" fontSize={{ base: '4xl', md: '6xl' }} mb={6}>
                          Experience Our Live Session
                        </Heading>
                        <Text fontSize="xl" opacity={0.9} mb={8} maxW="2xl">
                          See how we transform complex GenAI concepts into practical skills
                        </Text>
                      </MotionBox>

                      <MotionBox
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                      >
                        <Box
                          p={8}
                          variant="solid.300"
                          bgOpacity={0.1}
                          backdropFilter="blur(10px)"
                          borderRadius="2xl"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                        >
                          <HStack spacing={6} justify="center">
                            <Icon as={FaClock} boxSize={8} color="accent.gold" />
                            <VStack spacing={1} align="start">
                              <Heading size="lg">Duration: 90 Minutes</Heading>
                              <Text fontSize="md" opacity={0.9}>
                                Packed with interactive learning & hands-on practice
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>
                      </MotionBox>

                      <MotionBox
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                      >
                        <Button
                          variant="solid"
                          size="lg"
                          h="60px"
                          px={8}
                          rightIcon={<FaArrowRight />}
                          onClick={nextTab}
                          boxShadow="0 8px 30px rgba(16,185,129,0.4)"
                          _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 40px rgba(16,185,129,0.5)',
                          }}
                        >
                          Explore Session Flow
                        </Button>
                      </MotionBox>
                    </VStack>
                  </Container>
                </Box>
              </TabPanel>

              {/* Step 2: Timeline */}
              <TabPanel p={8}>
                <VStack spacing={8} align="stretch">
                  <Box textAlign="center">
                    <Heading size="lg" color="brand.800" mb={3}>
                      Here is the timeline for the session
                    </Heading>
                    <Text color="brand.500" fontSize="lg">
                      Each step is designed to ensure you have the time to learn and practice.
                    </Text>
                  </Box>
                  {/* Phase Timeline */}
                  <VStack spacing={6} align="stretch">
                    {sessionPhases.map((phase, index) => (
                      <MotionBox
                        key={phase.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        onMouseEnter={() => setHoveredPhase(phase.id)}
                        onMouseLeave={() => setHoveredPhase(null)}
                      >
                        <Box
                          p={8}
                          bg={hoveredPhase === phase.id ? 'white' : 'brand.50'}
                          borderRadius="2xl"
                          boxShadow={hoveredPhase === phase.id ?
                            '0 12px 40px rgba(16,185,129,0.2)' : '0 4px 20px rgba(0,0,0,0.08)'}
                          transition="all 0.3s"
                          _hover={{ transform: 'translateY(-4px)' }}
                          border="2px solid"
                          borderColor={hoveredPhase === phase.id ? 'accent.green' : 'transparent'}
                        >
                          <HStack spacing={6} align="start">
                            {/* Phase Number & Icon */}
                            <VStack spacing={2}>
                              <MotionCircle
                                size="60px"
                                bg="brand.700"
                                color="white"
                                animate={hoveredPhase === phase.id ? { scale: 1.1 } : { scale: 1 }}
                              >
                                <Icon as={phase.icon} boxSize={6} />
                              </MotionCircle>
                              <Badge
                                px={4}
                                py={1}
                                bg="accent.gold"
                                color="brand.800"
                                fontSize="sm"
                                fontWeight="700"
                                borderRadius="full"
                              >
                                {phase.duration}
                              </Badge>
                            </VStack>

                            {/* Phase Content */}
                            <VStack flex={1} align="start" spacing={4}>
                              <Heading size="md" color="brand.800">
                                {index + 1}. {phase.title}
                              </Heading>
                              <Text color="brand.600" fontSize="md">
                                {phase.description}
                              </Text>
                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} w="full">
                                {phase.details.map((detail, idx) => (
                                  <HStack key={idx} spacing={3}>
                                    <Icon as={FaCheck} color="accent.green" boxSize={4} />
                                    <Text fontSize="sm" color="brand.500">
                                      {detail}
                                    </Text>
                                  </HStack>
                                ))}
                              </SimpleGrid>

                              {/* Quiz Explanation */}
                              {phase.title === 'Quiz & Review' && (
                                <Box
                                  mt={4}
                                  p={4}
                                  bg="white"
                                  bgOpacity={0.1}
                                  border={1}
                                  borderRadius="xl"
                                  borderLeft="4px solid"
                                  borderColor="accent.green"
                                >
                                  <Text fontSize="sm" >
                                    <strong>Why we do this:</strong> We explain why wrong answers are incorrect to reinforce learning and prevent common mistakes.
                                  </Text>
                                </Box>
                              )}

                            </VStack>
                          </HStack>

                        </Box>
                      </MotionBox>
                    ))}
                    <Button
                      variant="solid"
                      size="lg"
                      h="60px"
                      px={8}
                      rightIcon={<FaArrowRight />}
                      onClick={nextTab}
                      boxShadow="0 8px 30px rgba(16,185,129,0.4)"
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 40px rgba(16,185,129,0.5)',
                      }}
                    >
                      Explore the concept flow
                    </Button>

                  </VStack>
                </VStack>
              </TabPanel>

              {/* Step 3: Concept Flow */}
              <TabPanel p={8}>
                <VStack spacing={8} align="stretch">
                  <Box textAlign="center">
                    <Heading size="lg" color="brand.800" mb={3}>
                      How We Teach Each Concept
                    </Heading>
                    <Text color="brand.500" fontSize="lg">
                      Our proven 8-step methodology ensures deep understanding
                    </Text>
                  </Box>

                  {/* Concept Flow Diagram */}
                  <Box position="relative" py={8}>
                    <HStack
                      spacing={0}
                      justify="space-between"
                      position="relative"
                      display={{ base: 'none', md: 'flex' }}
                    >
                      {/* Connecting Line */}
                      <Box
                        position="absolute"
                        top="40px"
                        left="50px"
                        right="50px"
                        h="2px"
                        bg="accent.green"
                        zIndex={0}
                      />

                      {conceptFlow.map((item, index) => (
                        <MotionBox
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          position="relative"
                          zIndex={1}
                        >
                          <VStack spacing={4} w="120px">
                            <MotionCircle
                              size="70px"
                              bg="white"
                              border="3px solid"
                              borderColor="accent.green"
                              whileHover={{ scale: 1.1, borderColor: '#FACC15' }}
                              cursor="pointer"
                              boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                            >
                              <Icon as={item.icon} boxSize={6} color="accent.green" />
                            </MotionCircle>
                            <VStack spacing={1}>
                              <Text
                                fontSize="sm"
                                fontWeight="700"
                                color="brand.800"
                                textAlign="center"
                              >
                                {item.step}
                              </Text>
                              <Text
                                fontSize="xs"
                                color="brand.500"
                                textAlign="center"
                              >
                                {item.desc}
                              </Text>
                            </VStack>
                          </VStack>
                        </MotionBox>
                      ))}
                    </HStack>

                    {/* Mobile: Vertical Flow */}
                    <VStack spacing={4} display={{ base: 'flex', md: 'none' }}>
                      {conceptFlow.map((item, index) => (
                        <MotionBox
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          w="full"
                        >
                          <Box
                            p={6}
                            bg="white"
                            borderRadius="2xl"
                            boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                            _hover={{ transform: 'translateY(-2px)' }}
                            transition="all 0.3s"
                          >
                            <HStack spacing={4}>
                              <Circle size="60px" bg="accent.green" color="white">
                                <Icon as={item.icon} boxSize={6} />
                              </Circle>
                              <VStack align="start" spacing={1} flex={1}>
                                <Text fontSize="md" fontWeight="700" color="brand.800">
                                  {index + 1}. {item.step}
                                </Text>
                                <Text fontSize="sm" color="brand.500">
                                  {item.desc}
                                </Text>
                              </VStack>
                            </HStack>
                          </Box>
                        </MotionBox>
                      ))}
                    </VStack>
                  </Box>
                  <Button
                    variant="solid"
                    size="lg"
                    h="60px"
                    px={8}
                    rightIcon={<FaArrowRight />}
                    onClick={nextTab}
                    boxShadow="0 8px 30px rgba(16,185,129,0.4)"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(16,185,129,0.5)',
                    }}
                  >
                    Next: Mini project

                  </Button>
                </VStack>
              </TabPanel>

              {/* Step 4: Mini-Project Breakdown */}
              <TabPanel p={8}>
                <VStack spacing={8} align="stretch">
                  <Box textAlign="center">
                    <Heading size="lg" color="brand.800" mb={3}>
                      Hands-On Mini-Project Structure
                    </Heading>
                    <Text color="brand.500" fontSize="lg">
                      Every session includes building a real-world application
                    </Text>
                  </Box>

                  {/* Project Phases */}
                  <VStack spacing={6} align="stretch">
                    {[
                      {
                        number: 1,
                        title: 'Problem Context',
                        description: 'We start with a real-world business problem. Understand why this solution matters and who needs it.',
                        badges: ['Real use case', 'Industry relevance', 'Problem statement'],
                        borderColor: 'accent.green'
                      },
                      {
                        number: 2,
                        title: 'Step-by-Step Implementation',
                        description: 'Live coding session where we build the solution together. Each step explained with rationale.',
                        items: [
                          'Code architecture setup',
                          'Core logic implementation',
                          'Integration & testing',
                          'Error handling & optimization'
                        ],
                        borderColor: 'accent.gold'
                      },
                      {
                        number: 3,
                        title: 'Input → Output Demonstration',
                        description: 'See the solution in action. We show various inputs and analyze outputs together.',
                        borderColor: 'accent.green'
                      }
                    ].map((phase, index) => (
                      <MotionBox
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.2 }}
                      >
                        <Box
                          p={8}
                          bg="white"
                          borderRadius="2xl"
                          boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                          borderLeft="4px solid"
                          borderColor={phase.borderColor}
                          _hover={{ transform: 'translateY(-4px)' }}
                          transition="all 0.3s"
                        >
                          <HStack spacing={6} align="start">
                            <Circle size="60px" bg="brand.700" color="white" flexShrink={0}>
                              <Text fontWeight="900" fontSize="xl">{phase.number}</Text>
                            </Circle>
                            <VStack align="start" spacing={4} flex={1}>
                              <Heading size="md" color="brand.800">
                                {phase.title}
                              </Heading>
                              <Text color="brand.600" fontSize="md" lineHeight="1.7">
                                {phase.description}
                              </Text>

                              {phase.badges && (
                                <HStack spacing={3} flexWrap="wrap">
                                  {phase.badges.map((badge, idx) => (
                                    <Badge
                                      key={idx}
                                      colorScheme="green"
                                      variant="subtle"
                                      px={3}
                                      py={1}
                                      fontSize="xs"
                                    >
                                      {badge}
                                    </Badge>
                                  ))}
                                </HStack>
                              )}

                              {phase.items && (
                                <VStack align="start" spacing={2} pl={4}>
                                  {phase.items.map((item, idx) => (
                                    <HStack key={idx} spacing={3}>
                                      <Icon as={FaCheck} color="accent.green" boxSize={4} />
                                      <Text fontSize="sm" color="brand.500">{item}</Text>
                                    </HStack>
                                  ))}
                                </VStack>
                              )}

                              {phase.number === 3 && (
                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                                  <Box p={4} bg="accent.blue" borderRadius="xl">
                                    <Text fontSize="sm" fontWeight="700" color="brand.50" mb={2}>
                                      INPUT
                                    </Text>
                                    <Text fontSize="sm" color="brand.50">
                                      Sample data, user queries, or test cases
                                    </Text>
                                  </Box>
                                  <Box p={1} textAlign="center">
                                    <Icon as={FaPlay} boxSize={20} color={'grey'} />

                                  </Box>
                                  <Box p={4} bg="magenta" bgOpacity={0.1} borderRadius="xl">
                                    <Text fontSize="sm" fontWeight="700" color="brand.50" mb={2}>
                                      OUTPUT
                                    </Text>
                                    <Text fontSize="sm" color="brand.50">
                                      Results, visualizations, or generated content
                                    </Text>
                                  </Box>
                                </SimpleGrid>
                              )}

                            </VStack>
                          </HStack>
                        </Box>
                      </MotionBox>
                    ))}
                    <Button
                      variant="solid"
                      size="lg"
                      h="60px"
                      px={8}
                      rightIcon={<FaArrowRight />}
                      onClick={nextTab}
                      boxShadow="0 8px 30px rgba(16,185,129,0.4)"
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 40px rgba(16,185,129,0.5)',
                      }}
                    >
                      Know why it matters

                    </Button>

                  </VStack>
                </VStack>
              </TabPanel>

              {/* Step 5: Why It Matters */}
              <TabPanel p={8}>
                <VStack spacing={8} align="stretch">
                  <Box textAlign="center">
                    <Heading size="lg" color="brand.800" mb={3}>
                      Why This Matters
                    </Heading>
                    <Text color="brand.500" fontSize="lg">
                      Real skills that transform your career
                    </Text>
                  </Box>

                  {/* Benefits Grid */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {[
                      {
                        icon: FaBrain,
                        title: 'Real-World Experience',
                        description: 'Work on industry-relevant problems and learn best practices'
                      },
                      {
                        icon: FaGraduationCap,
                        title: 'Structured Curriculum',
                        description: 'Follow a proven learning path with clear milestones'
                      },
                      {
                        icon: FaLightbulb,
                        title: 'Innovation Skills',
                        description: 'Develop problem-solving and creative thinking abilities'
                      }
                    ].map((benefit, index) => (
                      <MotionBox
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                      >
                        <Box
                          p={6}
                          bg="white"
                          borderRadius="2xl"
                          boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                          textAlign="center"
                          _hover={{ transform: 'translateY(-4px)' }}
                          transition="all 0.3s"
                          h="full"
                        >
                          <VStack spacing={4}>
                            <Circle size="70px" bg="accent.green" bgOpacity={0.1} color="accent.green">
                              <Icon as={benefit.icon} boxSize={6} />
                            </Circle>
                            <Heading size="md" color="brand.800">
                              {benefit.title}
                            </Heading>
                            <Text fontSize="sm" color="brand.600" lineHeight="1.6">
                              {benefit.description}
                            </Text>
                          </VStack>
                        </Box>
                      </MotionBox>
                    ))}
                  </SimpleGrid>

                  {/* Final CTA */}
                  <MotionBox
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    <Box
                      p={8}
                      bg="brand.800"
                      borderRadius="2xl"
                      color="white"
                      textAlign="center"
                    >
                      <VStack spacing={6}>
                        <Heading size="lg">
                          This is just ONE session. Imagine 24 weeks of this interactive, project-based learning!
                        </Heading>
                        <Text fontSize="lg" opacity={0.9}>
                          Join our comprehensive program and transform your career with hands-on GenAI skills
                        </Text>
                        <HStack spacing={4} justify="center">
                          <Button
                            as="a"
                            href="/webinar"
                            variant="solid"
                            size="lg"
                            h="60px"
                            px={8}
                            onClick={onClose}
                            boxShadow="0 8px 30px rgba(16,185,129,0.4)"
                            _hover={{
                              transform: 'translateY(-2px)',
                              boxShadow: '0 12px 40px rgba(16,185,129,0.5)',
                            }}
                          >
                            Enroll for Demo Class
                          </Button>

                        </HStack>
                      </VStack>
                    </Box>
                  </MotionBox>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SessionFlowModal;